import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Divider
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  Timeline as TimelineIcon,
  TableChart as TableChartIcon,
  PeopleAlt as PeopleIcon,
  Event as EventIcon,
  Assessment as AssessmentIcon,
  MedicalServices as MedicalIcon,
  AttachMoney as MoneyIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import { useNotification } from '../../contexts/NotificationContext';

// Lista de reportes disponibles
const reportes = [
  {
    id: 'dashboard',
    title: 'Dashboard Ejecutivo',
    description: 'Vista general con indicadores clave y gráficos resumen',
    icon: <AssessmentIcon />,
    category: 'general'
  },
  {
    id: 'pacientes-especialidad',
    title: 'Pacientes por Especialidad',
    description: 'Distribución de pacientes según especialidad',
    icon: <PieChartIcon />,
    category: 'pacientes'
  },
  {
    id: 'citas-mensuales',
    title: 'Citas Mensuales',
    description: 'Evolución del número de citas por mes',
    icon: <TimelineIcon />,
    category: 'citas'
  },
  {
    id: 'citas-especialista',
    title: 'Citas por Especialista',
    description: 'Distribución de citas atendidas por cada especialista',
    icon: <BarChartIcon />,
    category: 'citas'
  },
  {
    id: 'pacientes-nuevos',
    title: 'Pacientes Nuevos',
    description: 'Evolución de altas de pacientes por periodo',
    icon: <PeopleIcon />,
    category: 'pacientes'
  },
  {
    id: 'citas-estado',
    title: 'Citas por Estado',
    description: 'Distribución de citas según su estado (programadas, realizadas, canceladas)',
    icon: <EventIcon />,
    category: 'citas'
  },
  {
    id: 'ingresos-mensuales',
    title: 'Ingresos Mensuales',
    description: 'Detalle de ingresos generados por mes',
    icon: <MoneyIcon />,
    category: 'financiero'
  },
  {
    id: 'evaluaciones-realizadas',
    title: 'Evaluaciones Realizadas',
    description: 'Detalle de evaluaciones neuropsicológicas realizadas',
    icon: <MedicalIcon />,
    category: 'clinico'
  },
  {
    id: 'listado-pacientes',
    title: 'Listado de Pacientes',
    description: 'Lista completa de pacientes con información básica',
    icon: <TableChartIcon />,
    category: 'listados'
  },
  {
    id: 'listado-citas',
    title: 'Listado de Citas',
    description: 'Lista detallada de citas con filtros personalizables',
    icon: <TableChartIcon />,
    category: 'listados'
  }
];

// Categorías de reportes
const categories = {
  'general': 'General',
  'pacientes': 'Pacientes',
  'citas': 'Citas',
  'financiero': 'Financiero',
  'clinico': 'Clínico',
  'listados': 'Listados'
};

const ReportesList = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('general');

  // Filtrar reportes por categoría
  const filteredReportes = reportes.filter(reporte => 
    activeCategory === 'todos' || reporte.category === activeCategory
  );

  // Navegar al reporte seleccionado
  const handleReporteClick = (reporteId) => {
    navigate(`/reportes/${reporteId}`);
  };

  // Exportar reporte (ejemplo - en realidad abriría el diálogo)
  const handleExportClick = (e, reporteId) => {
    e.stopPropagation(); // Evitar navegación al reporte
    showNotification('Funcionalidad de exportación en desarrollo', 'info');
  };

  // Renderizar lista de categorías
  const renderCategoriesList = () => {
    return (
      <Paper elevation={0} sx={{ height: '100%' }}>
        <List component="nav" sx={{ p: 0 }}>
          <ListItem>
            <Typography variant="subtitle1" fontWeight="bold">
              Categorías
            </Typography>
          </ListItem>
          <Divider />
          <ListItemButton
            selected={activeCategory === 'todos'}
            onClick={() => setActiveCategory('todos')}
          >
            <ListItemText primary="Todos los reportes" />
          </ListItemButton>
          {Object.entries(categories).map(([key, value]) => (
            <ListItemButton
              key={key}
              selected={activeCategory === key}
              onClick={() => setActiveCategory(key)}
            >
              <ListItemText primary={value} />
            </ListItemButton>
          ))}
        </List>
      </Paper>
    );
  };

  // Renderizar tarjetas de reportes
  const renderReporteCards = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" my={5}>
          <CircularProgress />
        </Box>
      );
    }

    if (filteredReportes.length === 0) {
      return (
        <Box p={3} textAlign="center">
          <Typography variant="body1" color="textSecondary">
            No hay reportes disponibles en esta categoría.
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={3}>
        {filteredReportes.map((reporte) => (
          <Grid item xs={12} sm={6} md={4} key={reporte.id}>
            <Paper
              elevation={1}
              sx={{
                p: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-4px)'
                }
              }}
              onClick={() => handleReporteClick(reporte.id)}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
                  {reporte.icon}
                </ListItemIcon>
                <Typography variant="h6" noWrap>
                  {reporte.title}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2, flexGrow: 1 }}>
                {reporte.description}
              </Typography>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  {categories[reporte.category]}
                </Typography>
                <Button
                  startIcon={<DownloadIcon />}
                  size="small"
                  onClick={(e) => handleExportClick(e, reporte.id)}
                >
                  Exportar
                </Button>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <PageHeader
        title="Reportes"
        breadcrumbs={[
          { text: 'Dashboard', to: '/dashboard' },
          { text: 'Reportes' }
        ]}
      />

      <Grid container spacing={3}>
        {/* Sidebar de categorías */}
        <Grid item xs={12} md={3}>
          {renderCategoriesList()}
        </Grid>

        {/* Contenido principal */}
        <Grid item xs={12} md={9}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5">
                  {activeCategory === 'todos' 
                    ? 'Todos los reportes' 
                    : `Reportes de ${categories[activeCategory]}`}
                </Typography>
              </Box>
              {renderReporteCards()}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReportesList;